<template>
  <section class="slider" v-if="item">
    <div class="wrap">
      <div class="controls">
        <div class="year">
          <div ref="year">
            {{ item.year }}
          </div>
        </div>

        <div class="prev-next" v-if="items.length > 1">
          <div class="prev" @click.prevent="go(-1)">
            <icon-slide-arrow />
          </div>
          <div class="next" @click.prevent="go(1)">
            <icon-slide-arrow />
          </div>
        </div>
      </div>
      <div class="content" ref="content">
        <div>
          <span class="year-label">
            {{ item.year }}
          </span>
          <h4>{{ item.title }}</h4>
          <p>{{ item.description }}</p>
        </div>
        <div class="content-wrapper">
          <div class="content-box" ref="contentinfoone">
            <h2>{{ item.count }}</h2>
            <span>Initiativer</span>
          </div>
          <div class="content-box" ref="contentinfotwo">
            <h2>{{ item.amount }}</h2>
            <span>DKK</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Initiatives from '@/models/initiatives'
import iconSlideArrow from '@icons/icon-slide-arrow'
import { TimelineLite, Power2 } from 'gsap'

export default {
  components: { iconSlideArrow },
  data: () => ({
    current: 0,
    tweening: false,
    items: []
  }),
  async created () {
    this.items = await Initiatives.get()
  },
  methods: {
    go (direction) {
      if (this.tweening) {
        return
      }

      this.tweening = true

      const length = this.items.length
      const request = this.current + direction

      let next = request < 0 ? length - 1 : (request > (length - 1) ? 0 : request)
      const ease = Power2.easeInOut

      let tl = new TimelineLite({
        onComplete: () => {
          this.current = next
          setTimeout(() => { tl.reverse() }, 155)
        },
        onReverseComplete: () => {
          this.tweening = false
        }
      })
      tl.to(this.$refs.year, 0.366, { opacity: 0, x: '-5px', skewX: '-0.5', ease }, 'start')
      tl.to(this.$refs.content, 0.366, { y: 5, skewY: '0.1', opacity: 0, ease }, 'start')
    }
  },
  computed: {
    item () {
      return this.items.length ? this.items[this.current] : null
    }
  }
}
</script>

<style lang="scss" scoped>
/* Import from Spar Nord Assets */
@import '~@styles/global/index';
@import '~@styles/colors/variables';
@import '~@styles/mq/variables';
@import '~@styles/mixins/index';
@import '~@animations/easings/variables';
@import '~@animations/durations/variables';

.slider {
  background-color: $color-grey-x;

  margin-top: $global-padding * 8;
}

.slider .wrap {
  position: relative;
  padding-top: $global-padding * 9;
  padding-bottom: $global-padding * 7;
}

.controls {
  position: absolute;
  top: 0;
  right: 0;

  &:before {
    content: "";
    display: block;
    background-color: $color-grey-x;
    z-index: 1;
    height: 50%;
    width: 200%;
    transform: translateX(-50%);
    position: absolute;
  }

  .year, .prev, .next {
    height: 80px;
    background: $color-grey-xx;

    transform: translateY(-100%);
  }

  .year {
    background-color: $color-red;
    color: $color-white;

    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;

    transform: translateX(-50%) translateY(-100%);

    @include font-size(18px, 28px);
    font-weight: 600;
  }

  .prev-next {
    display: flex;
    z-index: 2;
    position: relative;
  }

  .prev, .next {
    width: 80px;

    display: flex;
    text-align: center;
    align-items: center;
    justify-content: center;

    -webkit-tap-highlight-color: $color-gold;

    cursor: pointer;

    &:hover {
      @media #{$above-breakpoint-xs} {
        background-color: $color-white;
      }
    }

    svg {
      transition: transform $duration-normal $easeing-OutExpo;
    }
  }

  .prev {
    svg {
      transform: rotate(90deg) scale(0.75);
    }

    &:hover {
      svg {
        animation: arrow-out-left $duration-slow $easeing-InOutExpo forwards $duration-fast;
      }
    }
  }

  .next {
    svg {
      transform: rotate(-90deg) scale(0.75);
    }

    &:hover {
      svg {
        animation: arrow-out-right $duration-slow $easeing-InOutExpo forwards $duration-fast;
      }
    }
  }
}

.content {
  position: relative;

  @media #{$above-breakpoint-1024} {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;

    > div {
      flex: 0 1 calc(50% - 6.3%);

      @at-root .internet-explorer #{&} {
        flex: 0 1 40%;
      }
    }
  }

  .year-label {
    position: absolute;
    left: 0;
    top: 0;

    color: $color-white;
    @include font-size(100px, 130px);
    font-weight: 900;

    transform: translate(-15%, -15%);

    @media #{$below-breakpoint-768} {
      transform: translate(-5%, -78%);
    }
  }

  h4 {
    @include font-size(24px, 30px);
    line-height: 1.1;
    font-weight: 700;

    position: relative;
    z-index: 1;

    margin-bottom: $global-padding * 1.5;

    &:before {
      content: "";
      display: block;
      margin-bottom: $global-padding;

      background: $color-red;
      height: 10px;
      width: 74px;
    }
  }

  p {
    @include font-size(16px, 18px);
    line-height: 1.4;

    position: relative;
    z-index: 1;

    + p {
      margin-top: $global-padding;
    }
  }
}

.content-box {
  text-align: center;
  flex-wrap: wrap;

  &:first-child {
    margin-bottom: $global-padding * 4;

    @media #{$below-breakpoint-1024} {
      margin-top: $global-padding * 4;
      margin-bottom: $global-padding;
    }
  }

  h2 {
    @include font-size(40px, 80px);
    font-weight: bold;
  }

  span {
    @include font-size(25px, 40px);
    color: $color-red;
    font-weight: 900;
  }
}

/* Animations */
@keyframes arrow-out-right {
  0% {
    transform: rotate(-90deg) scale(0.75) translateY(0);
    opacity: 1;
  }

  50% {
    transform: rotate(-90deg) scale(0.75) translateY(100%);
    opacity: 0;
  }

  50.1% {
    transform: rotate(-90deg) scale(0.75) translateY(-100%);
    opacity: 0;
  }

  100% {
    transform: rotate(-90deg) scale(0.75) translateY(0);
    opacity: 1;
  }
}

@keyframes arrow-out-left {
  0% {
    transform: rotate(90deg) scale(0.75) translateY(0);
    opacity: 1;
  }

  50% {
    transform: rotate(90deg) scale(0.75) translateY(100%);
    opacity: 0;
  }

  50.1% {
    transform: rotate(90deg) scale(0.75) translateY(-100%);
    opacity: 0;
  }

  100% {
    transform: rotate(90deg) scale(0.75) translateY(0);
    opacity: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/sections/slider.vue